/**
 * Zentrale Siegel-Definitionen (Single Source of Truth)
 *
 * Diese Datei ist die EINZIGE Quelle für Siegel-Definitionen.
 * Alle anderen Dateien (server/seals.js, src/seals.js) importieren von hier.
 *
 * Verwendung:
 * - Server: const { SEALS } = require('../src/shared/entities/seals');
 * - Client: import { SEALS } from './shared/entities/seals';
 *
 * Neue Siegel hier hinzufügen - sie erscheinen automatisch überall.
 *
 * HINWEIS: Rarity-Definitionen kommen aus rarity.js (Single Source of Truth)
 */

// Import unified rarity system
// Note: This uses require() which works in both Node.js and bundlers
const rarityModule = require('./rarity');
const {
  RARITY,
  RARITY_CONFIG,
  getRarityLabel,
  getSellPriceForRarity,
  getRarityDisplay,
} = rarityModule;

// Backwards-compatible alias
const SEAL_RARITY = RARITY;

// Backwards-compatible: RARITY_LABELS derived from RARITY_CONFIG
const RARITY_LABELS = Object.fromEntries(
  Object.entries(RARITY_CONFIG).map(([key, config]) => [key, config.label])
);

// Backwards-compatible: RARITY_SELL_PRICING derived from RARITY_CONFIG
const RARITY_SELL_PRICING = Object.fromEntries(
  Object.entries(RARITY_CONFIG).map(([key, config]) => [key, config.sellPrice])
);

const SEAL_CATEGORY = {
  ECONOMY: 'economy',
  TEMPO: 'tempo',
  POWER: 'power',
  ARMOUR: 'armour'
};

// Trigger types for seals
// Media-Player-Metapher: ⏸️ Pause, ▶️ Play, ⏫ Level Up, 🔽 Down
const SEAL_TRIGGER = {
  INSTANT: 'instant', // ▶️ Triggers when cards are played
  WIN: 'win',         // ⏫ Triggers only when the trick is won
  HAND: 'hand',       // ⏸️ Triggers while seal is on hand, stops when played
  BEATEN: 'beaten'    // 🔽 Triggers immediately when your card is overtaken by another player
};

// Seal Distribution Phase Configuration
const SEAL_DISTRIBUTION = {
  DURATION_MS: 30000,           // 30 Sekunden Timer
  AUTO_ATTACH_CURSE: true,      // Curse-Siegel automatisch anheften
  MIN_TIMER_WARNING: 5000,      // Timer wird bei < 5s rot
};

// Display configuration for triggers (Single Source of Truth)
// Colors match shop seals (SealCard.css) for visual consistency
// Unicode-Dreiecke für Trigger-Typen:
// - ◼ Quadrat = Hand (wartet)
// - ▶ Play = Instant (spielt)
// - ▲ Up = Win (Erfolg)
// - ▼ Down = Beaten (überboten, Trost)
const SEAL_TRIGGER_DISPLAY = {
  [SEAL_TRIGGER.INSTANT]: {
    icon: '▶',
    label: 'Gespielt',
    description: 'wenn gespielt',
    cssClass: 'trigger-instant',
    colors: { from: '#6ee7b7', to: '#228b22' } // Emerald/Green (matches shop)
  },
  [SEAL_TRIGGER.WIN]: {
    icon: '▲',
    label: 'Gewonnen',
    description: 'bei Stich-Gewinn',
    cssClass: 'trigger-win',
    colors: { from: '#f5d76e', to: '#b8860b' } // Gold (matches shop)
  },
  [SEAL_TRIGGER.HAND]: {
    icon: '◼',
    label: 'Gehalten',
    description: 'solange auf der Hand',
    cssClass: 'trigger-hand',
    colors: { from: '#93c5fd', to: '#3b82f6' } // Blue (matches shop)
  },
  [SEAL_TRIGGER.BEATEN]: {
    icon: '▼',
    label: 'Überboten',
    description: 'sofort wenn deine Karte überboten wird',
    cssClass: 'trigger-beaten',
    colors: { from: '#f87171', to: '#b22222' } // Red (matches shop)
  }
};

// Backwards-compatible: RARITY_DISPLAY derived from RARITY_CONFIG
const RARITY_DISPLAY = Object.fromEntries(
  Object.entries(RARITY_CONFIG).map(([key, config]) => [
    key,
    { color: config.color, label: config.label, cssClass: config.cssClass, glow: config.animated ? 'animated' : (config.glow !== 'none' ? 'light' : 'none') }
  ])
);

/**
 * Get display info for a trigger type
 * @param {string} trigger - 'instant', 'win', or 'hand'
 * @returns {Object} { icon, label, description }
 */
function getTriggerDisplay(trigger) {
  return SEAL_TRIGGER_DISPLAY[trigger] || { icon: '❓', label: trigger, description: trigger };
}

// getRarityDisplay is now imported from rarity.js

/**
 * Alle verfügbaren Siegel (62 Stück)
 *
 * Verteilung:
 * - Common (13): coin, dump, quickShot, donation, middleFinger, opener, wheelOfFortune,
 *                streetKid, colorLoyalty, smallFry, pairSeal, consolation, taschenlampe
 * - Uncommon (16): gold, tax, poison, multBoost, shield, sneakPeak, swap, passiveIncome, closer,
 *                  timedBonus, flushGordon, paygap, tripwire, revenge, follower, schmarotzer
 * - Rare (19): platinum, heist, phoenix, stresser, milkGlass, sealRing, magnet, watcher, hotStreak,
 *              streetAce, tripsBonus, fullHouseMaster, fullThrottle, impudence, martyr, dogshit,
 *              lueckenfueller, gluecksklee, vulkan
 * - Legendary (6): jackpot, megaMult, fortress, grandFinale, theTwo, zeitkapsel
 * - Curse (8): draw, leech, vulture, lameDuck, brakeShoe, trojanHorse, schnorrerUli, parasite
 *
 * Trigger:
 * - instant: Effekt bei Kartenspielen
 * - win: Effekt nur bei Stich-Gewinn
 * - hand: Passiver Effekt solange auf der Hand, Strafe beim Spielen
 * - beaten: Effekt sofort wenn deine Karte überboten wird (nicht erst bei Stich-Ende!)
 */
const SEALS = {
  // === COMMON (7) ===
  COIN: {
    id: 'coin',
    name: 'Schnelle Mark',
    category: SEAL_CATEGORY.ECONOMY,
    rarity: SEAL_RARITY.COMMON,
    trigger: SEAL_TRIGGER.INSTANT,
    description: '+5 Münzen beim Spielen',
    detailedDescription: 'Sofortiges Bargeld! Sobald du diese Karte spielst, erhältst du +5 Münzen – egal ob du die Runde gewinnst oder nicht. Perfekt um schnell den Shop-Geldbeutel zu füllen.',
    icon: '💰',
    effect: { type: 'instantCoins', value: 5 },
    messages: {
      trigger: { self: 'Du erhältst +5 💰', other: '{player} erhält +5 💰' },
    }
  },
  DUMP: {
    id: 'dump',
    name: 'Ballast',
    category: SEAL_CATEGORY.TEMPO,
    rarity: SEAL_RARITY.COMMON,
    trigger: SEAL_TRIGGER.WIN,
    description: '1 Karte abwerfen',
    detailedDescription: 'Entrümple deine Hand! Wenn du mit dieser Karte einen Stich gewinnst, wirfst du automatisch deine niedrigste Handkarte ab. So wirst du schneller fertig und los ungewollte Karten.',
    icon: '🗑️',
    effect: { type: 'dump', value: 1 },
    messages: {
      trigger: { self: 'Du wirfst 1 Karte ab', other: '{player} wirft 1 Karte ab' },
    }
  },
  DRAW: {
    id: 'draw',
    name: 'Nachschub',
    category: SEAL_CATEGORY.TEMPO,
    rarity: SEAL_RARITY.CURSE,
    trigger: SEAL_TRIGGER.WIN,
    description: 'Ziehe 1 Karte',
    detailedDescription: 'Fluch-Siegel! Wenn du mit dieser Karte einen Stich gewinnst, musst du 1 Karte vom Stapel ziehen. Mehr Karten = länger zum Fertigwerden = schlechtere Platzierung. Als Sabotage für Arsch/Vize-Arsch gedacht!',
    icon: '📥',
    effect: { type: 'draw', value: 1 },
    messages: {
      trigger: { self: 'Du ziehst 1 Karte', other: '{player} zieht 1 Karte' },
    }
  },
  QUICK_SHOT: {
    id: 'quickShot',
    name: 'Schnellschuss',
    category: SEAL_CATEGORY.ECONOMY,
    rarity: SEAL_RARITY.COMMON,
    trigger: SEAL_TRIGGER.INSTANT,
    description: '+5 Münzen beim Spielen',
    detailedDescription: 'Blitzschnelles Geld! Wie die Schnelle Mark gibt dir dieses Siegel sofort +5 Münzen beim Ausspielen der Karte. Der Unterschied? Nur das Icon – sammle beide für maximalen Cash!',
    icon: '⚡',
    effect: { type: 'instantCoins', value: 5 },
    messages: {
      trigger: { self: 'Du erhältst +5 💰', other: '{player} erhält +5 💰' },
    }
  },
  DONATION: {
    id: 'donation',
    name: 'Vorkasse',
    category: SEAL_CATEGORY.ECONOMY,
    rarity: SEAL_RARITY.COMMON,
    trigger: SEAL_TRIGGER.INSTANT,
    description: 'Du -1💰, Stich-Gewinner +7💰',
    detailedDescription: 'Du zahlst 1💰, aber wer den Stich gewinnt, erhält +7💰. Für Leute mit Überzeugung',
    icon: '💶',
    effect: { type: 'donation', selfCost: 1, winnerBonus: 7 },
    messages: {
      trigger: { self: 'Du zahlst 1 💰, Gewinner +7 💰', other: '{player} zahlt! Gewinner +7 💰' },
    }
  },
  THE_TWO: {
    id: 'theTwo',
    name: 'Pommesgabel',
    category: SEAL_CATEGORY.POWER,
    rarity: SEAL_RARITY.LEGENDARY,
    trigger: SEAL_TRIGGER.INSTANT,
    description: '+2 ⭐️, +0.05 Mult sofort',
    detailedDescription: 'Rock on! Beim Ausspielen erhältst du sofort +2 ⭐️ UND +0.05 permanenten Multiplikator – egal ob du den Stich gewinnst. Eine seltene Kombi aus ⭐️ und Mult in einem Siegel!',
    icon: '🤘',
    effect: { type: 'instantPoints', value: 2, multBoost: 0.05 },
    messages: {
      trigger: { self: 'Pommesgabel! +2 ⭐, +0.05 Mult', other: '{player} zeigt die Pommesgabel! +2 ⭐' },
    }
  },
  MIDDLE_FINGER: {
    id: 'middleFinger',
    name: 'Mittelfinger',
    category: SEAL_CATEGORY.TEMPO,
    rarity: SEAL_RARITY.COMMON,
    trigger: SEAL_TRIGGER.INSTANT,
    description: 'Alle Kartenfarben werden bis zum nächsten Stich als 🖕 angezeigt',
    detailedDescription: 'Hier kommt der Proll durch: Wenn du diese Karte spielst, werden ALLE Kartenfarben auf dem Bildschirm als 🖕 angezeigt – bis die Runde endet. Ein deutlicher Gruß in die Runde.',
    icon: '🖕',
    effect: { type: 'middleFinger' },
    messages: {
      trigger: { self: 'Alle Farben sind jetzt 🖕!', other: '{player} zeigt den Mittelfinger! Alle Farben sind 🖕' },
    }
  },
  OPENER: {
    id: 'opener',
    name: 'Rampensau',
    category: SEAL_CATEGORY.ECONOMY,
    rarity: SEAL_RARITY.COMMON,
    trigger: SEAL_TRIGGER.INSTANT,
    description: '+5💰, wenn du damit eine Runde eröffnest.',
    detailedDescription: 'Wer zuerst kommt, kassiert! Du erhältst +5 Münzen, aber NUR wenn du mit dieser Karte eine neue Runde eröffnest (erster Zug nach einem Stich). Verpufft wirkungslos, wenn jemand anderes die Runde begonnen hat.',
    icon: '🐷',
    effect: { type: 'opener', value: 5 },
    messages: {
      trigger: { self: 'Rampensau! +5 💰 für den ersten Zug!', other: '{player} drängelt sich vor! +5 💰' },
      noEffect: { self: 'Rampensau verpufft - nicht der erste Zug der Runde', other: null },
    }
  },
  WHEEL_OF_FORTUNE: {
    id: 'wheelOfFortune',
    name: 'Glücksrad',
    category: SEAL_CATEGORY.ECONOMY,
    rarity: SEAL_RARITY.COMMON,
    trigger: SEAL_TRIGGER.WIN,
    description: 'Drehe am Glücksrad!',
    detailedDescription: 'Du drehst für alle sichtbar am Rad. Du kannst Punkte, Münzen und Multiplikatoren gewinnen, eine Karten abwerfen oder ziehen müssen.',
    icon: '🎡',
    effect: { type: 'wheelOfFortune' },
    messages: {
      trigger: { self: 'Du drehst am Glücksrad!', other: '{player} dreht am Glücksrad!' },
    }
  },

  // === UNCOMMON (8) ===
  GOLD: {
    id: 'gold',
    name: 'Goldgrube',
    category: SEAL_CATEGORY.ECONOMY,
    rarity: SEAL_RARITY.UNCOMMON,
    trigger: SEAL_TRIGGER.INSTANT,
    description: '+8 Münzen beim Spielen',
    detailedDescription: 'Der große Bruder der Schnellen Mark! +8 Münzen sofort beim Ausspielen – unabhängig vom Stich-Ausgang. Teurer im Shop, aber das Investment lohnt sich!',
    icon: '🏆',
    effect: { type: 'instantCoins', value: 8 },
    messages: {
      trigger: { self: 'Du erhältst +8 💰', other: '{player} erhält +8 💰' },
    }
  },
  TAX: {
    id: 'tax',
    name: 'Finanzamt',
    category: SEAL_CATEGORY.ECONOMY,
    rarity: SEAL_RARITY.UNCOMMON,
    trigger: SEAL_TRIGGER.WIN,
    description: 'Gegner -1 Münze, Du +2',
    detailedDescription: 'Der Staat kassiert mit! Bei Stich-Gewinn bekommst du +2 Münzen UND jeder Gegner verliert -1 Münze. Effektiv +5 Münzen Vorteil in einem 4-Spieler-Spiel – das Finanzamt schlägt wieder zu!',
    icon: '💸',
    effect: { type: 'steal', value: 2, steal: 1 },
    messages: {
      trigger: { self: 'Du kassierst +{value} 💰, Gegner -{steal} 💰', other: '{player} kassiert Steuern! -{steal} 💰 für dich' },
    }
  },
  POISON: {
    id: 'poison',
    name: 'Giftzahn',
    category: SEAL_CATEGORY.ECONOMY,
    rarity: SEAL_RARITY.UNCOMMON,
    trigger: SEAL_TRIGGER.INSTANT,
    description: 'Stich-Gewinner verliert 2 Münzen',
    detailedDescription: 'Vergifteter Sieg! Wer auch immer den Stich gewinnt (auch du selbst!), verliert -2 Münzen. INSTANT-Trigger = wirkt beim Spielen. Taktik: Auf mittlere Karten setzen, mit denen andere gewinnen – oder als Drohung nutzen!',
    icon: '☠️',
    effect: { type: 'poison', value: 2 },
    messages: {
      trigger: { self: 'Du legst Gift! Gewinner -2 💰', other: '{player} legt Gift! Gewinner -2 💰' },
    }
  },
  MULT_BOOST: {
    id: 'multBoost',
    name: 'Verstärker',
    category: SEAL_CATEGORY.POWER,
    rarity: SEAL_RARITY.UNCOMMON,
    trigger: SEAL_TRIGGER.WIN,
    description: '+0,1 Multiplikator dauerhaft',
    detailedDescription: 'Dein Multiplikator wächst! Wenn du mit dieser Karte einen Stich gewinnst, steigt dein Mult permanent um +0,1. Der Bonus bleibt das ganze Spiel über – auch in folgenden Partien!',
    icon: '📈',
    effect: { type: 'multBoost', value: 0.1, persistent: true },
    messages: {
      trigger: { self: 'Dein Mult steigt um +0,1 dauerhaft', other: '{player}: Mult +0,1 dauerhaft' },
    }
  },
  SHIELD: {
    id: 'shield',
    name: 'Schutzschild',
    category: SEAL_CATEGORY.ARMOUR,
    rarity: SEAL_RARITY.UNCOMMON,
    trigger: SEAL_TRIGGER.WIN,
    description: 'Blockiert 1x Zwangs-Ziehen',
    detailedDescription: 'Einmal-Schutz! Nach einem Stich-Gewinn mit dieser Karte bist du EINMAL vor Zwangs-Ziehen geschützt. Wenn du passen musst, wird das Schild verbraucht statt eine Karte zu ziehen. Kann spielentscheidend sein!',
    icon: '🛡️',
    effect: { type: 'shield', persistent: true, uses: 1 },
    messages: {
      trigger: { self: 'Du erhältst ein Schild', other: '{player} erhält ein Schild' },
      enable: { self: '🛡️ Schild bereit!', other: '{player} hat ein Schild!' },
      use: { self: 'Dein Schild blockt Zwangs-Ziehen!', other: '{player}s Schild blockt!' },
    }
  },
  SNEAK_PEAK: {
    id: 'sneakPeak',
    name: 'Sneak Peak',
    category: SEAL_CATEGORY.TEMPO,
    rarity: SEAL_RARITY.UNCOMMON,
    trigger: SEAL_TRIGGER.WIN,
    description: 'Zeigt 3x die oberste Karte des Stapels',
    detailedDescription: 'Wissen ist Macht! Nach einem Stich-Gewinn kannst du DREIMAL die oberste Karte des Nachziehstapels ansehen. Perfekt um zu entscheiden, ob Passen sich lohnt oder ob du lieber eine Karte ziehst!',
    icon: '👁️',
    effect: { type: 'peek', persistent: true, uses: 3 },
    messages: {
      trigger: { self: 'Du kannst jetzt spähen', other: '{player} kann jetzt spähen' },
      enable: { self: '👁️ Du kannst spähen!', other: '{player} kann spähen!' },
      use: { self: 'Du siehst die oberste Karte: {card}', other: '{player} späht den Stapel' },
    }
  },
  SWAP: {
    id: 'swap',
    name: 'Tauschhandel',
    category: SEAL_CATEGORY.TEMPO,
    rarity: SEAL_RARITY.UNCOMMON,
    trigger: SEAL_TRIGGER.WIN,
    description: 'Tausche 1 Handkarte gegen eine zufällige aus dem Stapel',
    detailedDescription: 'Glücksspiel-Tausch! Nach einem Stich-Gewinn kannst du EINMAL eine beliebige Handkarte gegen eine zufällige vom Stapel tauschen. Riskant aber lohnend – tausche nervige Karten weg und hoffe auf bessere!',
    icon: '🔄',
    effect: { type: 'swap', persistent: true, uses: 1 },
    messages: {
      trigger: { self: 'Du kannst jetzt tauschen', other: '{player} kann jetzt tauschen' },
      enable: { self: '🔄 Tausch bereit!', other: '{player} kann tauschen!' },
      use: { self: 'Du tauschst 1 Karte', other: '{player} tauscht 1 Karte' },
    }
  },
  CLOSER: {
    id: 'closer',
    name: 'Rausschmeißer',
    category: SEAL_CATEGORY.ECONOMY,
    rarity: SEAL_RARITY.UNCOMMON,
    trigger: SEAL_TRIGGER.WIN,
    description: '+4 Münzen wenn du damit gewinnend rausgehst',
    detailedDescription: 'Der krönende Abschluss! +4 Münzen, wenn du mit dieser Karte als LETZTER Karte deiner Hand einen Stich gewinnst. Hefte es an eine starke Karte – z.B. eine 2 oder Ass – mit der du die Runde beenden willst!',
    icon: '🚪',
    effect: { type: 'closer', value: 4, coins: true },
    messages: {
      trigger: { self: 'Rausschmeißer! +4 💰 zum Abschied!', other: '{player} geht mit +4 💰!' },
      noEffect: { self: 'Rausschmeißer verpufft - noch Karten übrig', other: null },
    }
  },

  // === RARE (9) ===
  PLATINUM: {
    id: 'platinum',
    name: 'Platinbarren',
    category: SEAL_CATEGORY.ECONOMY,
    rarity: SEAL_RARITY.RARE,
    trigger: SEAL_TRIGGER.INSTANT,
    description: '+12 Münzen beim Spielen',
    detailedDescription: 'Der Rolls-Royce unter den Münz-Siegeln! Satte +12 Münzen beim Ausspielen – ohne Bedingungen, ohne Risiko. Teuer im Shop, aber ein garantierter Geldsegen. Perfekt um größere Einkäufe zu finanzieren!',
    icon: '💎',
    effect: { type: 'instantCoins', value: 12 },
    messages: {
      trigger: { self: 'Du erhältst +12 💰', other: '{player} erhält +12 💰' },
    }
  },
  HEIST: {
    id: 'heist',
    name: 'Raubzug',
    category: SEAL_CATEGORY.ECONOMY,
    rarity: SEAL_RARITY.RARE,
    trigger: SEAL_TRIGGER.WIN,
    description: 'Gegner -3 Münzen, Du +3',
    detailedDescription: 'Der große Raub! Bei Stich-Gewinn bekommst du +3 Münzen UND JEDER Gegner verliert -3 Münzen. Effektiv +12 Münzen Vorteil in einem 4-Spieler-Spiel – aber nur bei Gewinn! Aggro-Siegel für dominante Spieler.',
    icon: '🦹',
    effect: { type: 'steal', value: 3, steal: 3 },
    messages: {
      trigger: { self: 'Du raubst +{value} 💰, Gegner -{steal} 💰!', other: '{player} raubt! -{steal} 💰 für dich' },
    }
  },
  PHOENIX: {
    id: 'phoenix',
    name: 'Phönix',
    category: SEAL_CATEGORY.POWER,
    rarity: SEAL_RARITY.RARE,
    trigger: SEAL_TRIGGER.INSTANT,
    description: '+0,2 Mult wenn du Letzter bist',
    detailedDescription: 'Aus der Asche zum Sieg! +0,2 permanenter Mult beim Ausspielen, ABER nur wenn du aktuell auf dem letzten Platz (wenigste ⭐️) bist. Das Comeback-Siegel für Underdogs – je weiter hinten, desto wertvoller!',
    icon: '🔥',
    effect: { type: 'phoenixMult', value: 0.2 },
    messages: {
      trigger: { self: 'Phönix! +0,2 Mult!', other: '{player} erhebt sich! +0,2 Mult!' },
      noEffect: { self: 'Phönix verpufft - du bist nicht Letzter', other: '{player}s Phönix verpufft' },
    }
  },
  STRESSER: {
    id: 'stresser',
    name: 'Stresser',
    category: SEAL_CATEGORY.TEMPO,
    rarity: SEAL_RARITY.RARE,
    trigger: SEAL_TRIGGER.INSTANT,
    description: '+2💰, Zugzeit auf 10 Sek. bis Stich-Ende',
    detailedDescription: 'Unter Druck! Du bekommst +2 Münzen, ABER alle Spieler haben ab sofort nur noch 10 Sekunden Zugzeit bis zum Stich-Ende. Chaos-Siegel für schnelle Denker – langsame Gegner machen unter Zeitdruck Fehler!',
    icon: '⏱️',
    effect: { type: 'stresser', coins: 2, turnTimeLimit: 10 },
    messages: {
      trigger: { self: 'Stresser! +2💰, Timer auf 10s!', other: '{player} stresst! Timer auf 10s!' },
    }
  },
  MILK_GLASS: {
    id: 'milkGlass',
    name: 'Milchglas',
    category: SEAL_CATEGORY.POWER,
    rarity: SEAL_RARITY.RARE,
    trigger: SEAL_TRIGGER.INSTANT,
    description: '70%: +0,1 Mult. 30%: Karte zerbricht.',
    detailedDescription: 'Hohes Risiko, hohe Belohnung! 70% Chance auf +0,1 permanenten Mult beim Ausspielen. ABER: 30% Chance dass die Karte ZERBRICHT und aus dem Spiel entfernt wird! Das Glücksspiel-Siegel für Adrenalin-Junkies.',
    icon: '🥛',
    effect: { type: 'milkGlass', prePlay: true, successChance: 0.7, multBonus: 0.1 },
    messages: {
      trigger: { self: 'Milchglas hält! +0,1 Mult', other: '{player}s Milchglas hält! +0,1 Mult' },
      break: { self: '💔 Milchglas zerbricht! Karte verloren!', other: '{player}s Karte zerbricht!' },
    }
  },

  // === LEGENDARY (3) ===
  JACKPOT: {
    id: 'jackpot',
    name: 'Jackpot',
    category: SEAL_CATEGORY.ECONOMY,
    rarity: SEAL_RARITY.LEGENDARY,
    trigger: SEAL_TRIGGER.INSTANT,
    description: '+18 Münzen beim Spielen',
    detailedDescription: 'Der Hauptgewinn! Unglaubliche +18 Münzen beim Ausspielen der Karte – völlig unabhängig vom Stich-Ausgang. Das teuerste Siegel im Shop, aber ein einziger Einsatz finanziert fast alles andere!',
    icon: '🎰',
    effect: { type: 'instantCoins', value: 18 },
    messages: {
      trigger: { self: 'JACKPOT! Du erhältst +18 💰!', other: '{player}: JACKPOT! +18 💰!' },
    }
  },
  MEGA_MULT: {
    id: 'megaMult',
    name: 'Mega-Verstärker',
    category: SEAL_CATEGORY.POWER,
    rarity: SEAL_RARITY.LEGENDARY,
    trigger: SEAL_TRIGGER.WIN,
    description: '+0,3 Multiplikator dauerhaft',
    detailedDescription: 'Der heilige Gral der Multiplikatoren! +0,3 permanenter Mult bei Stich-Gewinn – dreimal so stark wie der normale Verstärker. Ein einziger erfolgreicher Einsatz kann dein Endgame komplett verändern!',
    icon: '🚀',
    effect: { type: 'multBoost', value: 0.3, persistent: true },
    messages: {
      trigger: { self: 'Dein Mult steigt um +0,3 dauerhaft!', other: '{player}: Mult +0,3 dauerhaft!' },
    }
  },
  FORTRESS: {
    id: 'fortress',
    name: 'Bollwerk',
    category: SEAL_CATEGORY.ARMOUR,
    rarity: SEAL_RARITY.LEGENDARY,
    trigger: SEAL_TRIGGER.WIN,
    description: 'Nie wieder Zwangs-Ziehen in dieser Partie',
    detailedDescription: 'Absolute Verteidigung! Nach einem Stich-Gewinn mit dieser Karte bist du für den Rest der Partie IMMUN gegen Zwangs-Ziehen. Egal wie oft du passen musst – du musst nie wieder eine Karte ziehen!',
    icon: '🏰',
    effect: { type: 'fortress', persistent: true, uses: -1 },
    messages: {
      trigger: { self: 'Nie wieder Zwangs-Ziehen!', other: '{player} ist unantastbar!' },
      enable: { self: '🏰 Festung aktiv!', other: '{player} hat eine Festung!' },
    }
  },
  SEAL_RING: {
    id: 'sealRing',
    name: 'Siegelring',
    category: SEAL_CATEGORY.POWER,
    rarity: SEAL_RARITY.RARE,
    trigger: SEAL_TRIGGER.WIN,
    description: 'Versiegle bis zu 3 Handkarten mit zufälligen Siegeln',
    detailedDescription: 'Der Siegel-Multiplikator! Bei Stich-Gewinn kannst du EINMAL bis zu 3 deiner Handkarten mit ZUFÄLLIGEN Siegeln versehen. Glückssache was du bekommst, aber potenziell 3 Gratis-Siegel!',
    icon: '💍',
    effect: { type: 'sealRing', persistent: true, uses: 1 },
    messages: {
      trigger: { self: 'Du kannst jetzt versiegeln', other: '{player} kann jetzt versiegeln' },
      enable: { self: '💍 Siegelring bereit!', other: '{player} hat einen Siegelring!' },
      use: { self: 'Du versiegelst 3 Karten', other: '{player} versiegelt 3 Karten' },
    }
  },
  MAGNET: {
    id: 'magnet',
    name: 'Magnet',
    category: SEAL_CATEGORY.TEMPO,
    rarity: SEAL_RARITY.RARE,
    trigger: SEAL_TRIGGER.WIN,
    description: 'Ziehe die höchste Karte aus dem Stapel',
    detailedDescription: 'Anziehungskraft! Bei Stich-Gewinn kannst du EINMAL die HÖCHSTE Karte aus dem Nachziehstapel auf deine Hand ziehen. Kein Zufall – du bekommst garantiert die beste verfügbare Karte! Perfekt für starke Finisher.',
    icon: '🧲',
    effect: { type: 'magnet', persistent: true, uses: 1 },
    messages: {
      trigger: { self: 'Du kannst jetzt anziehen', other: '{player} kann jetzt anziehen' },
      enable: { self: '🧲 Magnet bereit!', other: '{player} hat einen Magneten!' },
      use: { self: 'Du ziehst die höchste Karte: {card}', other: '{player} zieht die höchste Karte' },
    }
  },
  LUECKENFUELLER: {
    id: 'lueckenfueller',
    name: 'Lückenfüller',
    category: SEAL_CATEGORY.POWER,
    rarity: SEAL_RARITY.RARE,
    trigger: SEAL_TRIGGER.WIN,
    description: 'Füllt bis zu 2 leere Joker-Plätze',
    detailedDescription: 'Gratis-Verstärkung! Wenn du mit dieser Karte einen Stich gewinnst, werden bis zu 2 deiner leeren Joker-Plätze automatisch mit zufälligen Pleb-Jokern gefüllt. Hast du schon 3 Joker? Dann passiert nichts.',
    icon: '🧩',
    effect: { type: 'gapFiller', maxJokers: 2 },
    messages: {
      trigger: { self: 'Lückenfüller! +{count} Joker', other: '{player} erhält {count} Joker' },
      noEffect: { self: 'Lückenfüller verpufft - keine Plätze frei', other: null },
    }
  },

  // === HAND SEALS (7) ===
  GLUECKSKLEE: {
    id: 'gluecksklee',
    name: 'Glücksklee',
    category: SEAL_CATEGORY.POWER,
    rarity: SEAL_RARITY.RARE,
    trigger: SEAL_TRIGGER.HAND,
    description: 'Pro eigenem Zug: +0.02 Mult. Beim Spielen: -0.03 Mult.',
    detailedDescription: 'Glück wächst langsam! HAND-Siegel: Jedes Mal wenn du am Zug bist, steigt dein Multiplikator um +0.02. ABER: Wenn du die Karte spielst, verlierst du -0.03 Mult als Strafe. Je länger du wartest, desto mehr Mult sammelst du!',
    icon: '🍀',
    effect: { type: 'gluecksklee', passive: { type: 'mult', value: 0.02 }, penalty: { type: 'mult', value: -0.03 } },
    messages: {
      passive: { self: 'Glücksklee! +0.02 Mult', other: null },
      trigger: { self: 'Glücksklee gespielt: -0.03 Mult', other: '{player} spielt Glücksklee: -0.03 Mult' },
    }
  },
  TASCHENLAMPE: {
    id: 'taschenlampe',
    name: 'Taschenlampe',
    category: SEAL_CATEGORY.ECONOMY,
    rarity: SEAL_RARITY.COMMON,
    trigger: SEAL_TRIGGER.HAND,
    description: 'Pro eigenem Zug: +2 Münzen, enthüllt zufällige Handkarte. Beim Spielen: -4 Münzen.',
    detailedDescription: 'Licht und Schatten! HAND-Siegel: Du kassierst +2 Münzen pro Zug, ABER eine zufällige Handkarte wird allen Gegnern im Event-Log gezeigt. Beim Spielen verlierst du -4 Münzen. Risiko-Einkommen mit Informationsleck!',
    icon: '🔦',
    effect: { type: 'taschenlampe', passive: { type: 'coinsAndReveal', value: 2 }, penalty: { type: 'coins', value: -4 } },
    messages: {
      passive: { self: 'Taschenlampe! +2 💰', other: null },
      trigger: { self: 'Taschenlampe gespielt: -4 💰', other: '{player} spielt Taschenlampe: -4 💰' },
    }
  },
  SCHMAROTZER: {
    id: 'schmarotzer',
    name: 'Schmarotzer',
    category: SEAL_CATEGORY.ECONOMY,
    rarity: SEAL_RARITY.UNCOMMON,
    trigger: SEAL_TRIGGER.HAND,
    description: '+1 Münze bei jedem fremden Stichgewinn. Beim Spielen: -1 Münze.',
    detailedDescription: 'Parasitäres Einkommen! HAND-Siegel: Jedes Mal wenn ein ANDERER Spieler einen Stich gewinnt, bekommst du +1 Münze. Beim Spielen verlierst du -1 Münze. Profitiere vom Erfolg der anderen!',
    icon: '🪱',
    effect: { type: 'schmarotzer', passive: { type: 'onOtherTrickWin', value: 1 }, penalty: { type: 'coins', value: -1 } },
    messages: {
      passive: { self: 'Schmarotzer! +1 💰', other: null },
      trigger: { self: 'Schmarotzer gespielt: -1 💰', other: '{player} spielt Schmarotzer: -1 💰' },
    }
  },
  VULKAN: {
    id: 'vulkan',
    name: 'Vulkan',
    category: SEAL_CATEGORY.POWER,
    rarity: SEAL_RARITY.RARE,
    trigger: SEAL_TRIGGER.HAND,
    description: 'Pro eigenem Zug: +1 Hitze. Beim Spielen: Hitze → Punkte, ziehe 1 Karte.',
    detailedDescription: 'Brodelnde Kraft! HAND-Siegel: Pro Zug sammelt sich +1 Hitze an. Wenn du die Karte spielst, wird alle Hitze 1:1 in ⭐️ umgewandelt – ABER du musst 1 Karte vom Stapel ziehen. Lass es brodeln für maximale Punkte!',
    icon: '🌋',
    effect: { type: 'vulkan', passive: { type: 'accumulate', resource: 'heat', value: 1 }, conversion: { ratio: 1, output: 'points' }, penalty: { type: 'draw', value: 1 } },
    messages: {
      passive: { self: 'Vulkan brodelt! +1 🔥', other: null },
      trigger: { self: 'Vulkan bricht aus! +{value} ⭐, ziehe 1 Karte', other: '{player}s Vulkan bricht aus! +{value} ⭐' },
    }
  },
  ZEITKAPSEL: {
    id: 'zeitkapsel',
    name: 'Zeitkapsel',
    category: SEAL_CATEGORY.ECONOMY,
    rarity: SEAL_RARITY.LEGENDARY,
    trigger: SEAL_TRIGGER.HAND,
    description: 'Pro eigenem Zug: +2 Münzen gespart. Beim Spielen: 75% ausgezahlt. Bei Partie-Ende: 100%.',
    detailedDescription: 'Geduldiges Sparen! HAND-Siegel: Pro Zug werden +2 Münzen angespart (nicht sofort ausgezahlt). Spielst du die Karte, bekommst du nur 75% der Ersparnisse. Am Partie-Ende werden 100% automatisch ausgezahlt. Geduld wird belohnt!',
    icon: '⏳',
    effect: { type: 'zeitkapsel', passive: { type: 'accumulate', resource: 'coins', value: 2 }, playoutRate: 0.75, partieEndRate: 1.0 },
    messages: {
      passive: { self: 'Zeitkapsel! +2 💰 gespart', other: null },
      trigger: { self: 'Zeitkapsel geöffnet! +{value} 💰', other: '{player} öffnet Zeitkapsel! +{value} 💰' },
    }
  },
  WATCHER: {
    id: 'watcher',
    name: 'Der Beobachter',
    category: SEAL_CATEGORY.TEMPO,
    rarity: SEAL_RARITY.RARE,
    trigger: SEAL_TRIGGER.HAND,
    description: 'Sieh die oberste Stapelkarte. Beim Spielen: Ziehe 2 Karten.',
    detailedDescription: 'Allwissend aber gefangen! HAND-Siegel: Solange auf deiner Hand, siehst du IMMER die oberste Stapelkarte. ABER: Wenn du diese Karte spielst, musst du 2 Karten als Strafe ziehen. Informationsvorsprung vs. Kartenballast!',
    icon: '👁️‍🗨️',
    effect: { type: 'watcher', passive: 'peek', penalty: { type: 'draw', value: 2 } },
    messages: {
      passive: { self: 'Du siehst die oberste Karte: {card}', other: null },
      trigger: { self: 'Du ziehst 2 Karten als Strafe', other: '{player} zieht 2 Karten Strafe' },
    }
  },
  PASSIVE_INCOME: {
    id: 'passiveIncome',
    name: 'Passive Einnahmen',
    category: SEAL_CATEGORY.ECONOMY,
    rarity: SEAL_RARITY.UNCOMMON,
    trigger: SEAL_TRIGGER.HAND,
    description: 'Pro eigenem Zug: +1 Münze. Beim Spielen: -3 Münzen.',
    detailedDescription: 'Geld verdienen durch Nichtstun! HAND-Siegel: Jedes Mal wenn du am Zug bist, bekommst du +1 Münze – einfach so! ABER: Wenn du die Karte spielst, verlierst du -3 Münzen als Strafe. Halte sie so lange wie möglich für maximalen Gewinn!',
    icon: '💹',
    effect: { type: 'passiveIncome', passive: { type: 'coins', value: 1 }, penalty: { type: 'coins', value: -3 } },
    messages: {
      passive: { self: 'Du erhältst +1 💰 passiv', other: null },
      trigger: { self: 'Du verlierst -3 💰 als Strafe', other: '{player} verliert -3 💰 Strafe' },
    }
  },

  // === CURSE SEALS (3) ===
  // Fluch-Siegel haben negative Effekte und werden an höchste Karten angeheftet.
  // Nur für Vize-Arsch & Arsch kaufbar - zur Sabotage via Exchange.
  LEECH: {
    id: 'leech',
    name: 'Blutegel',
    category: SEAL_CATEGORY.POWER,
    rarity: SEAL_RARITY.CURSE,
    trigger: SEAL_TRIGGER.WIN,
    description: '-2 ⭐️ beim Stich-Gewinn',
    detailedDescription: 'Fluch-Siegel! Wer diesen Stich gewinnt, VERLIERT 2 ⭐️. Kaufe es als Arsch/Vize-Arsch und hefte es an deine höchste Karte – die geht beim Kartentausch an den Präsidenten. Sabotage pur!',
    icon: '🧛',
    effect: { type: 'cursePoints', value: -2 },
    messages: {
      trigger: { self: 'Blutegel! Du verlierst -2 ⭐', other: '{player} verliert -2 ⭐ durch Blutegel' },
    }
  },
  VULTURE: {
    id: 'vulture',
    name: 'Pleitegeier',
    category: SEAL_CATEGORY.ECONOMY,
    rarity: SEAL_RARITY.CURSE,
    trigger: SEAL_TRIGGER.INSTANT,
    description: '-3 Münzen beim Spielen',
    detailedDescription: 'Fluch-Siegel! Der Spieler verliert -3 Münzen sobald er diese Karte ausspielt. Als Sabotage-Tool für Arsch/Vize-Arsch gedacht – hefte es an hohe Karten die du abgeben musst!',
    icon: '🦅',
    effect: { type: 'curseCoin', value: -3 },
    messages: {
      trigger: { self: 'Pleitegeier! Du verlierst -3 💰', other: '{player} verliert -3 💰 durch Pleitegeier' },
    }
  },
  LAME_DUCK: {
    id: 'lameDuck',
    name: 'Lahme Ente',
    category: SEAL_CATEGORY.TEMPO,
    rarity: SEAL_RARITY.CURSE,
    trigger: SEAL_TRIGGER.HAND,
    description: 'Karte zählt nicht für 5-Card-Tricks',
    detailedDescription: 'Fluch-Siegel! HAND-Trigger: Solange diese Karte auf deiner Hand ist, kann sie NICHT Teil eines 5-Karten-Zugs sein. Keine Straßen, keine Flushs mit dieser Ente! Ruiniert Kombo-Strategien.',
    icon: '🦆',
    effect: { type: 'lameDuck', excludeFrom5Card: true },
    messages: {
      passive: { self: 'Diese Karte ist lahm - keine 5-Card-Tricks!', other: null },
      trigger: { self: 'Lahme Ente watschelt davon', other: '{player}s Lahme Ente watschelt davon' },
    }
  },
  BRAKE_SHOE: {
    id: 'brakeShoe',
    name: 'Bremsschuh',
    category: SEAL_CATEGORY.POWER,
    rarity: SEAL_RARITY.CURSE,
    trigger: SEAL_TRIGGER.WIN,
    description: '-0,1 Multiplikator dauerhaft',
    detailedDescription: 'Fluch-Siegel! Bei Stich-Gewinn verlierst du -0,1 permanenten Mult – das ganze restliche Spiel! Der Anti-Verstärker: Macht Gewinne weniger wert. Perfekte Sabotage für hohe Karten, die du abgeben musst.',
    icon: '🥾',
    effect: { type: 'multBoost', value: -0.1, persistent: true },
    messages: {
      trigger: { self: 'Bremsschuh! Dein Mult sinkt um -0,1', other: '{player} verliert -0,1 Mult durch Bremsschuh' },
    }
  },
  TROJAN_HORSE: {
    id: 'trojanHorse',
    name: 'Trojanisches Pferd',
    category: SEAL_CATEGORY.ECONOMY,
    rarity: SEAL_RARITY.CURSE,
    trigger: SEAL_TRIGGER.WIN,
    description: '-4 Münzen beim Stich-Gewinn',
    detailedDescription: 'Fluch-Siegel! Bei Stich-Gewinn verlierst du -4 Münzen. Das Geschenk, das niemand will – sieht harmlos aus, kostet aber richtig Geld. Hefte es an deine besten Karten vor dem Kartentausch!',
    icon: '🎠',
    effect: { type: 'trojanHorse', value: -4 },
    messages: {
      trigger: { self: 'Trojanisches Pferd! Du verlierst -4 💰', other: '{player} verliert -4 💰 durch Trojanisches Pferd' },
    }
  },
  SCHNORRER_ULI: {
    id: 'schnorrerUli',
    name: 'Schnorrer-Uli',
    category: SEAL_CATEGORY.POWER,
    rarity: SEAL_RARITY.CURSE,
    trigger: SEAL_TRIGGER.WIN,
    description: '50% der Stich-⭐️ gehen an den Siegel-Geber',
    detailedDescription: 'Das hinterhältigste Fluch-Siegel! Bei Stich-Gewinn gehen 50% deiner ⭐️ an den Spieler, der dieses Siegel ursprünglich angeheftet hat. Perfekte Langzeit-Sabotage – du gewinnst, aber der Geber profitiert!',
    icon: '😏',
    effect: { type: 'schnorrerUli', splitPercent: 50 },
    messages: {
      trigger: { self: 'Schnorrer-Uli! Du verlierst 50% der ⭐️ an {originator}', other: '{player} verliert 50% an {originator} durch Schnorrer-Uli' },
      originator: { self: 'Schnorrer-Uli zahlt sich aus! +{value} ⭐ von {player}', other: '{originator} kassiert +{value} ⭐ durch Schnorrer-Uli' },
    }
  },
  HOT_STREAK: {
    id: 'hotStreak',
    name: 'Heißer Lauf',
    category: SEAL_CATEGORY.ECONOMY,
    rarity: SEAL_RARITY.RARE,
    trigger: SEAL_TRIGGER.WIN,
    description: '+2 Münzen pro vorherigem Stich-Gewinn in Folge',
    detailedDescription: 'Momentum ist alles! Bei Stich-Gewinn bekommst du +2 Münzen für JEDEN vorherigen Stich-Gewinn IN FOLGE. Erster Gewinn = +0, zweiter = +2, dritter = +4, usw. Das Schneeball-Siegel für dominante Spieler!',
    icon: '🏃‍♂️‍➡️',
    effect: { type: 'hotStreak', coinsPerStreak: 2 },
    messages: {
      trigger: { self: 'Heißer Lauf! +{value} 💰', other: '{player} ist im Flow! +{value} 💰' },
    }
  },

  // === TIMED SEALS (experimental) ===
  TIMED_BONUS: {
    id: 'timedBonus',
    name: 'Zeitbonus',
    category: SEAL_CATEGORY.ECONOMY,
    rarity: SEAL_RARITY.UNCOMMON,
    trigger: SEAL_TRIGGER.INSTANT,
    description: '+10 Münzen wenn innerhalb 3 Runden gespielt',
    detailedDescription: 'Tick-tack! Satte +10 Münzen beim Ausspielen, ABER nur wenn du die Karte innerhalb der ersten 3 Runden spielst. Danach verpufft das Siegel wirkungslos. Druck, aber lohnend – plane voraus!',
    icon: '⏰',
    effect: { type: 'timedCoins', value: 10, maxRounds: 3 },
    messages: {
      trigger: { self: 'Zeitbonus! +10 💰', other: '{player} kassiert Zeitbonus! +10 💰' },
      expired: { self: 'Zeitbonus abgelaufen...', other: '{player}s Zeitbonus ist abgelaufen' },
    }
  },

  // ============================================
  // === COMBO SEALS (Play-Type based) ===
  // ============================================

  // --- Straßen-Siegel ---
  STREET_KID: {
    id: 'streetKid',
    name: 'Straßenkind',
    category: SEAL_CATEGORY.ECONOMY,
    rarity: SEAL_RARITY.COMMON,
    trigger: SEAL_TRIGGER.INSTANT,
    description: '+3💰, +0.05 Mult wenn in Straße gespielt.',
    detailedDescription: 'Wenn diese Karte Teil einer Straße ist (inkl. Straight Flush und Randalaise Flush), bekommst du +3💰 UND +0.05 permanenten Mult. Ohne Straße passiert nichts – also taktisch auf Karten setzen, die du in Straßen spielst!',
    icon: '👶🏻',
    effect: { type: 'comboBonus', requiredPlayType: ['straight', 'straightFlush', 'royalFlush'], coins: 3, mult: 0.05 },
    messages: {
      trigger: { self: 'Straßenkind! +3 💰, +0.05 Mult', other: '{player} spielt Straßenkind! +3 💰' },
      noEffect: { self: 'Straßenkind verpufft - keine Straße', other: null },
    }
  },
  STREET_ACE: {
    id: 'streetAce',
    name: 'Straßenfeger',
    category: SEAL_CATEGORY.ECONOMY,
    rarity: SEAL_RARITY.RARE,
    trigger: SEAL_TRIGGER.WIN,
    description: '+9 Münzen wenn Straße den Stich gewinnt',
    detailedDescription: 'Der Straßen-Champion! +9 Münzen wenn du mit einer Straße (inkl. Straight Flush/Randalaise Flush) den Stich GEWINNST. Mächtiger als Straßenkind, aber nur bei Sieg. Hefte es auf Karten für deine stärksten Straßen!',
    icon: '🧹',
    effect: { type: 'comboWinBonus', requiredPlayType: ['straight', 'straightFlush', 'royalFlush'], coins: 9 },
    messages: {
      trigger: { self: 'Straßenfeger! +9 💰', other: '{player} fegt die Straße! +9 💰' },
      noEffect: { self: 'Straßenfeger verpufft - keine Straße', other: null },
    }
  },

  // --- Flush-Siegel ---
  FLUSH_GORDON: {
    id: 'flushGordon',
    name: 'Flush Gordon',
    category: SEAL_CATEGORY.ECONOMY,
    rarity: SEAL_RARITY.UNCOMMON,
    trigger: SEAL_TRIGGER.WIN,
    description: '+8 Münzen wenn Flush den Stich gewinnt',
    detailedDescription: 'Savior of the Universe! +8 Münzen wenn du mit einem Flush (5 Karten gleicher Farbe) den Stich gewinnst. Zählt auch für Straight Flush und Randalaise Flush. Hefte es auf Karten einer Farbe, die du oft in Flushs spielst!',
    icon: '👩‍🚀',
    effect: { type: 'comboWinBonus', requiredPlayType: ['flush', 'straightFlush', 'royalFlush'], coins: 8 },
    messages: {
      trigger: { self: 'Flush Gordon! +8 💰', other: '{player} flasht! +8 💰' },
      noEffect: { self: 'Flush Gordon verpufft - kein Flush', other: null },
    }
  },
  COLOR_LOYALTY: {
    id: 'colorLoyalty',
    name: 'Malermeister',
    category: SEAL_CATEGORY.ECONOMY,
    rarity: SEAL_RARITY.COMMON,
    trigger: SEAL_TRIGGER.INSTANT,
    description: '+2💰 pro Karte, wenn alle Karten rot sind',
    detailedDescription: 'Du kriegst +2💰 pro Karte, aber NUR wenn alle gespielten Karten rot sind (♥️♦️). Bei einem komplett roten Paar also +4💰, rotes Doppelpaar +8💰, rotes Flush: +10💰!',
    icon: '🎨',
    effect: { type: 'redCardBonus', coinsPerCard: 2 },
    messages: {
      trigger: { self: 'Malermeister! +{value} 💰', other: '{player} malt rot! +{value} 💰' },
      noEffect: { self: 'Malermeister verpufft - nicht alle Karten rot', other: null },
    }
  },

  // --- Drilling/Full-House-Siegel ---
  TRIPS_BONUS: {
    id: 'tripsBonus',
    name: 'Herr Zierlein',
    category: SEAL_CATEGORY.POWER,
    rarity: SEAL_RARITY.RARE,
    trigger: SEAL_TRIGGER.WIN,
    description: '+5 Münzen, +0.1 Mult bei Drilling-Gewinn',
    detailedDescription: 'Drilling zahlt sich aus! +5 Münzen UND +0,1 permanenter Mult wenn du mit einem Drilling (3 gleiche Karten) den Stich gewinnst. Seltene Kombination aus Cash und Mult – ideal für Drilling-Liebhaber!',
    icon: '👨‍🏫',
    effect: { type: 'comboWinBonus', requiredPlayType: ['triple'], coins: 5, mult: 0.1 },
    messages: {
      trigger: { self: 'Herr Zierlein! +5 💰, +0.1 Mult', other: '{player} drillt! +5 💰' },
      noEffect: { self: 'Herr Zierlein verpufft - kein Drilling', other: null },
    }
  },
  FULL_HOUSE_MASTER: {
    id: 'fullHouseMaster',
    name: 'Hausmeister',
    category: SEAL_CATEGORY.ECONOMY,
    rarity: SEAL_RARITY.RARE,
    trigger: SEAL_TRIGGER.WIN,
    description: '+6 Münzen bei Full-House-Gewinn',
    detailedDescription: 'Volles Haus, volle Kasse! +6 Münzen wenn du mit einem Full House (Drilling + Paar) den Stich gewinnst. Full Houses sind selten aber stark – dieses Siegel belohnt dich extra dafür!',
    icon: '👷🏼‍♂️',
    effect: { type: 'comboWinBonus', requiredPlayType: ['fullHouse'], coins: 6 },
    messages: {
      trigger: { self: 'Hausmeister! +6 💰', other: '{player} füllt das Haus! +6 💰' },
      noEffect: { self: 'Hausmeister verpufft - kein Full House', other: null },
    }
  },
  PAIR_SEAL: {
    id: 'pairSeal',
    name: 'Doppelpack',
    category: SEAL_CATEGORY.ECONOMY,
    rarity: SEAL_RARITY.COMMON,
    trigger: SEAL_TRIGGER.WIN,
    description: '+5 Münzen bei Paar-Gewinn',
    detailedDescription: 'Doppelt hält besser! Du bekommst +5 Münzen, wenn du mit einem Paar (zwei Karten gleichen Werts) den Stich gewinnst. Hefte es auf Karten, die du oft als Paar spielst!',
    icon: '🍻',
    effect: { type: 'comboWinBonus', requiredPlayType: ['pair'], coins: 5 },
    messages: {
      trigger: { self: 'Doppelpack! +5 💰', other: '{player} paart! +5 💰' },
      noEffect: { self: 'Doppelpack verpufft - kein Paar', other: null },
    }
  },

  // --- 5-Card-Play-Siegel ---
  FULL_THROTTLE: {
    id: 'fullThrottle',
    name: 'Vollgas',
    category: SEAL_CATEGORY.POWER,
    rarity: SEAL_RARITY.RARE,
    trigger: SEAL_TRIGGER.INSTANT,
    description: '+0.1 Mult bei 5-Card-Play',
    detailedDescription: 'Volle Power! +0,1 permanenter Mult beim Ausspielen, ABER nur wenn du genau 5 Karten spielst. Egal ob Straight, Flush, Full House oder beliebige 5 Karten – Hauptsache volle Hand!',
    icon: '🏎️',
    effect: { type: 'fiveCardBonus', mult: 0.1 },
    messages: {
      trigger: { self: 'Vollgas! +0.1 Mult', other: '{player} gibt Vollgas! +0.1 Mult' },
      noEffect: { self: 'Vollgas verpufft - kein 5-Card-Play', other: null },
    }
  },
  GRAND_FINALE: {
    id: 'grandFinale',
    name: 'Steil gehen',
    category: SEAL_CATEGORY.POWER,
    rarity: SEAL_RARITY.LEGENDARY,
    trigger: SEAL_TRIGGER.WIN,
    description: '+10 Münzen, +0.2 Mult bei 5-Card-Win',
    detailedDescription: 'Der ultimative Triumph! Wenn du mit einem 5-Karten-Zug den Stich GEWINNST, bekommst du unglaubliche +10 Münzen UND +0,2 permanenten Mult. Das stärkste Kombo-Siegel im Spiel – für den perfekten Moment!',
    icon: '🎆',
    effect: { type: 'fiveCardWinBonus', coins: 10, mult: 0.2 },
    messages: {
      trigger: { self: 'STEIL GEHEN! +10 💰, +0.2 Mult!', other: '{player} geht steil! +10 💰!' },
      noEffect: { self: 'Steil gehen verpufft - kein 5-Card-Win', other: null },
    }
  },

  // --- Rang-basierte Siegel ---
  SMALL_FRY: {
    id: 'smallFry',
    name: 'Kleinvieh',
    category: SEAL_CATEGORY.ECONOMY,
    rarity: SEAL_RARITY.COMMON,
    trigger: SEAL_TRIGGER.BEATEN,
    description: '+4 Münzen wenn überboten (nur Kartenwert ≤6)',
    detailedDescription: 'Kleinvieh macht auch Mist! +4 Münzen wenn deine Karte überboten wird, aber NUR wenn der Kartenwert 6 oder niedriger ist (3-6). BEATEN-Siegel – perfekt für schwache Karten, die sowieso geschlagen werden!',
    icon: '🐜',
    effect: { type: 'lowCardBonus', maxRank: 3, coins: 4 }, // rank 3 = card value 6
    messages: {
      trigger: { self: 'Kleinvieh! +4 💰', other: '{player} macht Mist zu Gold! +4 💰' },
      noEffect: { self: 'Kleinvieh verpufft - Karte zu hoch', other: null },
    }
  },

  // ============================================
  // === BEATEN SEALS (Trigger when overtaken) ===
  // ============================================
  PAYGAP: {
    id: 'paygap',
    name: 'Paygap',
    category: SEAL_CATEGORY.ECONOMY,
    rarity: SEAL_RARITY.UNCOMMON,
    trigger: SEAL_TRIGGER.BEATEN,
    description: '+1 Münze pro Rang-Differenz zur Karte die dich überboten hat',
    detailedDescription: 'Je größer der Unterschied, desto mehr Cash! BEATEN-Siegel: Sobald jemand deine Karte übertrumpft, bekommst du +1 Münze pro Rang-Differenz. Beispiel: Deine 5 wird von einer Dame überboten = +7 Münzen! Perfekt für niedrige Karten!',
    icon: '↕️',
    effect: { type: 'paygap', coinsPerRank: 1 },
    messages: {
      trigger: { self: 'Paygap! +{value} 💰 (Differenz: {diff})', other: '{player} kassiert Paygap! +{value} 💰' },
    }
  },
  CONSOLATION: {
    id: 'consolation',
    name: 'Trostpreis',
    category: SEAL_CATEGORY.ECONOMY,
    rarity: SEAL_RARITY.COMMON,
    trigger: SEAL_TRIGGER.BEATEN,
    description: '+3 Münzen wenn überboten',
    detailedDescription: 'Wer verliert, gewinnt! +3 Münzen sofort, wenn jemand deine Karte übertrifft. Ein BEATEN-Siegel – der Effekt triggert SOFORT wenn überboten, nicht erst am Stich-Ende. Perfekt für Karten, mit denen du sowieso nicht gewinnst!',
    icon: '🎁',
    effect: { type: 'consolation', coins: 3 },
    messages: {
      trigger: { self: 'Trostpreis! +3 💰', other: '{player} bekommt Trostpreis! +3 💰' },
    }
  },
  TRIPWIRE: {
    id: 'tripwire',
    name: 'Stolperfalle',
    category: SEAL_CATEGORY.TEMPO,
    rarity: SEAL_RARITY.UNCOMMON,
    trigger: SEAL_TRIGGER.BEATEN,
    description: 'Gewinner zieht 1 Karte',
    detailedDescription: 'Die Rache des Verlierers! BEATEN-Siegel: Sobald jemand deine Karte übertrumpft, muss der Stich-Gewinner 1 Karte ziehen. Eine fiese Falle – der Sieger wird bestraft! Perfekt auf Karten, die oft überboten werden.',
    icon: '🪤',
    effect: { type: 'tripwire', drawCards: 1 },
    messages: {
      trigger: { self: 'Stolperfalle! Gewinner zieht 1 Karte', other: '{player}s Stolperfalle! {winner} zieht 1 Karte' },
    }
  },
  REVENGE: {
    id: 'revenge',
    name: 'Bumerang',
    category: SEAL_CATEGORY.ECONOMY,
    rarity: SEAL_RARITY.UNCOMMON,
    trigger: SEAL_TRIGGER.BEATEN,
    description: 'Wer dich schlägt, verliert 2 Münzen',
    detailedDescription: 'Was du austeilst, kommt zurück! BEATEN-Siegel: Sobald jemand deine Karte übertrumpft, verliert diese Person -2 Münzen. Sieg hat seinen Preis!',
    icon: '🪃',
    effect: { type: 'revenge', coins: -2 },
    messages: {
      trigger: { self: 'Bumerang! Gegner -2 💰', other: '{player}s Bumerang trifft {winner}! -2 💰' },
    }
  },
  IMPUDENCE: {
    id: 'impudence',
    name: 'Impertinenz',
    category: SEAL_CATEGORY.POWER,
    rarity: SEAL_RARITY.RARE,
    trigger: SEAL_TRIGGER.BEATEN,
    description: '+0.1 Mult wenn überboten',
    detailedDescription: 'Trotz im Angesicht der Niederlage! BEATEN-Siegel: Sobald jemand deine Karte übertrumpft, bekommst du +0,1 permanenten Mult. Verlieren fühlt sich plötzlich wie Gewinnen an – langfristiger Mult-Aufbau!',
    icon: '🔥',
    effect: { type: 'impudence', mult: 0.1 },
    messages: {
      trigger: { self: 'Impertinenz! +0.1 Mult', other: '{player} ist impertinent! +0.1 Mult' },
    }
  },
  MARTYR: {
    id: 'martyr',
    name: 'Märtyrer',
    category: SEAL_CATEGORY.POWER,
    rarity: SEAL_RARITY.RARE,
    trigger: SEAL_TRIGGER.BEATEN,
    description: '+2 ⭐️ für ALLE Spieler außer dem Gewinner',
    detailedDescription: 'Selbstlos bis zum Schluss! BEATEN-Siegel: Wenn jemand deine Karte übertrumpft, bekommen ALLE Spieler außer dem Stich-Gewinner +2 ⭐️. Altruistisches Siegel – hilft den Underdogs gegen den Leader!',
    icon: '✝️',
    effect: { type: 'martyr', points: 2 },
    messages: {
      trigger: { self: 'Märtyrer! +2 ⭐ für alle außer Gewinner', other: '{player} opfert sich! +2 ⭐ für dich' },
    }
  },
  DOGSHIT: {
    id: 'dogshit',
    name: 'Hundescheiße',
    category: SEAL_CATEGORY.POWER,
    rarity: SEAL_RARITY.RARE,
    trigger: SEAL_TRIGGER.BEATEN,
    description: 'Gewinner verliert 0.05 Mult',
    detailedDescription: 'Reintreten macht keinen Spaß! BEATEN-Siegel: Wenn jemand deine Karte übertrumpft, verliert der Stich-Gewinner -0,05 permanenten Mult. Eine kleine aber dauerhafte Strafe – mehrere davon summieren sich!',
    icon: '💩',
    effect: { type: 'dogshit', mult: -0.05 },
    messages: {
      trigger: { self: 'Hundescheiße! Gewinner -0.05 Mult', other: '{player} hinterlässt Hundescheiße! {winner} -0.05 Mult' },
    }
  },

  // ============================================
  // === NEW HAND SEALS ===
  // ============================================
  FOLLOWER: {
    id: 'follower',
    name: 'Mitläufer',
    category: SEAL_CATEGORY.ECONOMY,
    rarity: SEAL_RARITY.UNCOMMON,
    trigger: SEAL_TRIGGER.HAND,
    description: '+1 Münze pro Zug wenn mind. 1 anderes Siegel auf Hand. Beim Spielen: -1 Münze',
    detailedDescription: 'Gesellig aber abhängig! HAND-Siegel: Jedes Mal wenn du am Zug bist und mindestens 1 ANDERE besiegelte Karte auf der Hand hast, bekommst du +1 Münze. Aber: Wenn du ihn spielst, verlierst du -1 Münze. Sammle Siegel und halte ihn so lange wie möglich!',
    icon: '🏃🏼‍♀️‍➡️',
    effect: { type: 'follower', passive: { type: 'conditionalCoins', value: 1, condition: 'otherSealOnHand' }, penalty: { type: 'coins', value: -1 } },
    messages: {
      passive: { self: 'Mitläufer! +1 💰', other: null },
      noEffect: { self: 'Mitläufer läuft allein - kein Bonus', other: null },
      trigger: { self: 'Mitläufer gespielt: -1 💰', other: '{player} spielt Mitläufer: -1 💰' },
    }
  },
  PARASITE: {
    id: 'parasite',
    name: 'Parasit',
    category: SEAL_CATEGORY.ECONOMY,
    rarity: SEAL_RARITY.CURSE,
    trigger: SEAL_TRIGGER.HAND,
    description: '-1 Münze pro eigenem Zug. Keine Strafe beim Spielen.',
    detailedDescription: 'Fluch-Siegel! HAND-Trigger: Jedes Mal wenn du am Zug bist, verlierst du -1 Münze solange diese Karte auf deiner Hand ist. ABER: Keine zusätzliche Strafe beim Spielen – werde ihn einfach los! Der langsame Blutsauger.',
    icon: '🦠',
    effect: { type: 'parasite', passive: { type: 'coins', value: -1 }, penalty: null },
    messages: {
      passive: { self: 'Parasit! -1 💰', other: null },
      trigger: { self: 'Parasit losgeworden!', other: '{player} wird Parasit los' },
    }
  }
};

// Helper functions

/**
 * Generate a unique key for seal accumulators (Vulkan, Zeitkapsel)
 * Key format: "playerIndex:rank:suit"
 * @param {number} playerIndex - Player index
 * @param {Object} card - Card object with rank and suit
 * @returns {string} Accumulator key
 */
function getSealAccumulatorKey(playerIndex, card) {
  return `${playerIndex}:${card.rank}:${card.suit}`;
}

function getSealById(id) {
  return Object.values(SEALS).find(s => s.id === id);
}

function getSealsByRarity(rarity) {
  return Object.values(SEALS).filter(s => s.rarity === rarity);
}

function getSealsByCategory(category) {
  return Object.values(SEALS).filter(s => s.category === category);
}

function getSealsByTrigger(trigger) {
  return Object.values(SEALS).filter(s => s.trigger === trigger);
}

function getAllSeals() {
  return Object.values(SEALS);
}

// CommonJS exports (bundler handles ESM conversion for client)
module.exports = {
  SEAL_RARITY,
  RARITY_LABELS,
  RARITY_SELL_PRICING,
  RARITY_DISPLAY,
  getRarityLabel,
  getRarityDisplay,
  getSellPriceForRarity,
  SEAL_CATEGORY,
  SEAL_TRIGGER,
  SEAL_TRIGGER_DISPLAY,
  SEAL_DISTRIBUTION,
  getTriggerDisplay,
  SEALS,
  getSealAccumulatorKey,
  getSealById,
  getSealsByRarity,
  getSealsByCategory,
  getSealsByTrigger,
  getAllSeals
};
